import { Suspense, useEffect, useMemo } from "react";
import { generatePath, Navigate, Outlet, useLocation, useParams } from "react-router";

import { cn } from "@/lib/chillnn/cn";
import { setHotelIdCookie } from "@/lib/chillnn/hotel-id-cookie";
import { useWindowSize } from "@/lib/react-use/use-window-size";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { SideMenuMobileContainer } from "@/components/features/side-menu/mobile";
import { SideMenuPCContainer } from "@/components/features/side-menu/pc";

import { CircleLoadingScreen } from "@/components/ui/loading/circle-screen";

import { GoogleFreeBookingLinksSettingsHotelTypeSelectionModal } from "@/components/features-new/settings-external-connection/google-free-booking-links/hotel-type-selection-modal";

import { FaqWidget } from "./faq-widget";

export const HotelIDLayout = () => {
	const { hotelID } = useParams();
	const { isLaptop } = useWindowSize();
	const shouldRenderSideMenuPC = useMemo(() => isLaptop, [isLaptop]);
	const { key } = useLocation();

	// support.chillnn.com と hotel_id を同期するため、施設切り替え（ルート遷移）のたびに
	// クロスサブドメイン cookie を更新する（ホスト管理画面には「現在の施設」を保持する
	// store が無く、このレイアウトが全施設スコープ画面の唯一の共通経路のためここで書き込む）
	useEffect(() => {
		if (!hotelID) return;
		setHotelIdCookie(hotelID);
	}, [hotelID]);

	if (!hotelID) {
		notifyUserEventInfo({
			variant: "error",
			title: "URLが正しくありません",
		});

		return <Navigate to={generatePath(PATHS.TOP)} />;
	}

	return (
		<div className="relative flex min-h-screen">
			{shouldRenderSideMenuPC ? (
				<div className="sticky top-0 z-10 block h-screen w-[250px] shrink-0 bg-white print:hidden">
					<SideMenuPCContainer hotelID={hotelID} />
				</div>
			) : (
				<div>
					<SideMenuMobileContainer hotelID={hotelID} />
				</div>
			)}

			<div
				className={cn(
					shouldRenderSideMenuPC
						? "w-full max-w-[calc(100%-250px)] bg-zinc-50 print:bg-white"
						: "w-full bg-zinc-50 pt-12 print:bg-white", // モバイルのヘッダーの高さ分のパディングを当てている
				)}
			>
				{/* @see https://github.com/remix-run/react-router/issues/10568#issuecomment-1582509468 */}
				{/* keyを付与することでルートごとにSuspenseのfallbackが表示される */}
				<Suspense
					key={key}
					fallback={
						<CircleLoadingScreen
							className={cn(shouldRenderSideMenuPC ? "w-[calc(100%-250px)]" : "w-full")}
						/>
					}
				>
					{/* ホテルタイプを選択してもらうためのモーダル */}
					<GoogleFreeBookingLinksSettingsHotelTypeSelectionModal hotelID={hotelID} />

					<div className={cn(!shouldRenderSideMenuPC)}>
						<Outlet />
					</div>
				</Suspense>
			</div>

			<FaqWidget />
		</div>
	);
};

HotelIDLayout.displayName = "HotelIDLayout";
